import React from "react";
import * as Thumbnails from "./Thumbnails";
import { Paths, Urls } from "./../constants/UrlConstants";
import { getContent } from "./Content";
import { getPhone, getOpeningHours } from "../helpers/ContentStateHelper";
import { Link } from "react-router-dom";
import WarningImage from "../../src/assets/img/info-blue.svg";
import layoutStyle from "../assets/css/modules/shared/layout.css";

const CoverDetailsLink = { Link: Paths.Cover, Text: "Cover Details" };
const MembershipDocsLink = {
  Link: Paths.CoverDocuments,
  Text: "Membership Documents"
};
const MakeChangesLink = {
  Link: Paths.MakeChangesToCover,
  Text: "Make Changes To Cover"
};

const RenewalLink = {
  Link: Paths.Renewal,
  Text: "My Renewal"
};

const HowToSeeWhoIsCovered = {
  Question: "How do I see who's covered?",
  Answer: {
    Any: (
      <React.Fragment>
        <p>
          If you're the lead member, you can view everyone who's covered under
          your plan on the Cover details page, under 'Other people on my plan'.
        </p>
        <p>
          For confidentiality, if you're a family member, you can only view the
          lead member's details.
        </p>
      </React.Fragment>
    ),
    HOL: (
      <React.Fragment>
        <p>
          If you're the policyholder, you can view everyone who's covered under
          your policy on the Cover details page, under 'Other people on my
          policy'.
        </p>
        <p>
          For confidentiality, if you're a family member, you can only view the
          policyholder's details.
        </p>
      </React.Fragment>
    )
  }
};

const WhoAreCovered = {
  Question: "Who are the people covered?",
  Answer: {
    Any: (
      <p>
        The people covered are the lead member and any family members who are
        covered by your membership and who share the same membership number.
      </p>
    ),
    HOL: (
      <p>
        The people covered are the policyholder and any family members who are
        covered by your membership and who share the same membership number.
      </p>
    )
  }
};

const HowDoIAddOtherOptions = {
  Question: "How do I add other options to my cover?",
  Answer: {
    Any: (props, params) => (
      <p>
        To amend the options on your plan, we'll need to speak to you. Please
        call our team of Personal Advisers on <strong>{getPhone(props)}</strong>
        . Lines are open {getOpeningHours(props)}
      </p>
    ),
    HOL: (props, params) => (
      <p>
        To amend the options on your policy, we'll need to speak to you. Please
        call our team of Personal Advisers on <strong>{getPhone(props)}</strong>
        . Lines are open {getOpeningHours(props)}
      </p>
    )
  }
};

const HowDoIAddOtherOptionsInd = {
  Question: "When and how can I make changes to my cover?",
  Answer: {
    Any: (props, params) => (
      <p>
        You can add options to your plan in the 14 days after you join us, or each year at your renewal once you’ve received your renewal documents.<br></br>
        <br></br>You can remove or reduce options or cancel your plan at any time during the year.<br></br>
        <br></br>To amend the options on your plan, we'll need to speak with you. <br></br><br></br>Please
        call us on <strong>{getPhone(props)}</strong>
        . Lines are open {getOpeningHours(props)}.
      </p>
    ),
    HOL: (props, params) => (
      <p>
        You can add options to your policy in the 14 days after you join us, or each year at your renewal once you’ve received your renewal documents.<br></br>
        <br></br>You can remove or reduce options or cancel your policy at any time during the year.<br></br>
        <br></br>To amend the options on your policy, we'll need to speak with you. <br></br><br></br>Please
        call us on <strong>{getPhone(props)}</strong>
        . Lines are open {getOpeningHours(props)}.
      </p>
    )
  }
};

const HowDoIFindHospital = {
  Question: "Can I find a hospital convenient for me?",
  Answer: {
    HOL: (
      <p>
        If you would like to know the location of hospitals covered under your
        policy, please view the{" "}
        <a
          className={layoutStyle.anchorDecorationUnderline}
          href={Urls.HOLDirectoryOfHospitals}
          rel="noopener noreferrer"
          target="_blank"
        >
          <strong>Directory of Hospitals.</strong>
        </a>{" "}
      </p>
    )
  }
};

export default {
  NavLinks: {
    Any: [CoverDetailsLink, MembershipDocsLink, MakeChangesLink],
    SME: (props, params) =>
      getContent(
        props,
        {
          Format2: [MembershipDocsLink],
          Any: [CoverDetailsLink, MembershipDocsLink]
        },
        params
      ),
    LargeCorporate: (props, params) =>
      getContent(
        props,
        {
          Format2: [MembershipDocsLink],
          Any: [CoverDetailsLink, MembershipDocsLink]
        },
        params
      ),
    Format2: [MembershipDocsLink, MakeChangesLink],
    Saga: [{ Link: Paths.MembershipInfoSaga, Text: "Membership Information" }],
    International: [MembershipDocsLink]
  },
  RenewalLink: RenewalLink,
  Heading: "Cover",
  CoverDetailsHeader: <h2>Cover details</h2>,
  CoverDetailsBody: {
    Any: (
      <p>
        Here you can find an overview of your cover, with a summary of your
        benefit entitlements and the extras you have available on your plan -
        including access to medical experts and discounts at our health store.
        You can also view your membership documents.
      </p>
    ),
    HOL: (
      <p>
        Here you can find an overview of your cover, with a summary of your
        benefit entitlements and the extras you have available on your policy -
        including access to medical experts and discounts at our health store.
        You can also view your membership documents.
      </p>
    )
  },
  CoverMembersBody: {
    Any: (
      <p>
        Here you can view the excess and cover details for yourself and anyone
        16 and under on your plan.
      </p>
    ),
    HOL: (
      <p>
        Here you can view the excess and cover details for yourself and anyone
        16 and under on your policy.
      </p>
    )
  },
  CoverMembersDependantBody: (
    <p>Here you can view your excess and cover details</p>
  ),
  ProductName: "Product name",
  MembershipNumber: "Membership number",
  PolicyStartDate: { Any: "Plan start date", HOL: "Policy start date" },
  RenewalDate: "Renewal date",
  OtherMembersOnYourPlan: {
    Any: "Other people on my plan",
    HOL: "Other people on my policy"
  },
  PlanDatesLabel: { Any: "Plan dates", HOL: "Policy dates" },
  ExcessTitle: "Excess",
  ExcessDescription:
    "Excess is your contribution to the cost of your treatment, paid for each membership year. Once it has been fully deducted in that year, you won’t need to pay towards treatment, provided you’re covered.",
  NewClaimTitle: "Get started with a new claim",
  TotalExcess: "My excess for this membership year: £",
  ExcessNotExist: {
    Any:
      "There is no excess on your plan therefore an excess deduction will not apply to eligible claims.",
    HOL:
      "There is no excess on your policy therefore an excess deduction will not apply to eligible claims."
  },
  ExcessUsedAmount: "Deducted so far: £",
  ExcessRemainingAmount: "Remaining for you to pay: £",
  DoubleExcessWarningImage: WarningImage,
  DoubleExcessWarning:
    "Please be aware that your excess is applied each membership year. As you're close to your renewal date, if you incur any costs in the next membership year – even if it's for the same condition – we'll deduct the excess from that claim.",
  LastUpdatedDateTimeDescription: "The details below were last updated on ",
  BenefitTitle: "Benefit entitlements",
  BenefitDescription: (
    <React.Fragment>
      This is a summary of your benefit entitlements. For more information,
      please see your{" "}
      <Link
        className={"hol-link " + layoutStyle.anchorDecorationUnderline}
        to={Paths.CoverDocuments}
      >
        membership documents
      </Link>
      .
    </React.Fragment>
  ),

  SixWeekOptionContent: (
    <React.Fragment>
      <p>Your plan includes the 6 week option.</p>
      <p>
        This means if you need in-patient treatment, day-patient treatment,
        cancer treatment (including radiotherapy or chemotherapy), surgery or a
        surgical procedure and your treatment or surgery is available on the NHS
        within six weeks of when it's needed, then you’ll use the NHS. If it
        isn’t, you’ll go private straightaway.
      </p>
    </React.Fragment>
  ),

  GuidedOptionDescription: (
    <React.Fragment>
      <p>
        Your plan includes the Guided option. This means you will use our Fast
        Track Appointment Service everytime you need to see a specialist.
      </p>
      <p>
        We'll do the work for you - sourcing up to three specialists, who focus
        on your condition. We'll book the appointment you choose at a time and
        place that's right for you.
      </p>
    </React.Fragment>
  ),
  TravelCoverTitle: "Travel",
  TravelCoverDescription: {
    Any: (
      <React.Fragment>
        <p>
          If you'd like to discuss any of the benefits on your Travel policy,
          please contact our Travel Team on <strong>01892 556 133</strong> or if
          you'd like to discuss a claim, please contact our Travel Claims
          helpline on <strong>0345 602 0303</strong>.
        </p>
      </React.Fragment>
    ),
    SMEModular: (
      <React.Fragment>
        <p>
          If you'd like to discuss any of the benefits on your Travel policy,
          please contact our Travel Team on <strong>01892 504 444</strong> or if
          you'd like to discuss a claim, please contact our Travel Claims
          helpline on <strong>0845 602 0303</strong>.
        </p>
      </React.Fragment>
    )
  },
  EAPEssentialTitle: "EAP - Essential",
  EAPPremierTitle: "EAP - Premier",
  EAPDescription: (
    <React.Fragment>
      <p>
        <p>
          Confidential employee support - When you want a little help with life’s twists and turns, you can speak in 
          confidence to an expert through our Employee Assistance Programme on{" "}
          <strong>0800 138 5472</strong> or visit{" "}
          <a
            className={layoutStyle.anchorDecorationUnderline}
            href={Urls.AXABeSupportedURL}
            rel="noopener noreferrer"
            target="_blank"
          >
           <strong>axabesupported.co.uk</strong> 
          </a>{" "}and enter access code: <strong>supported</strong> on your first visit.
        </p>
      </p>
    </React.Fragment>
  ),
  MentalHealth: {
    HOL:
      "Face to face, email or phone counselling sessions. Contact us on 0345 600 7696.",
    Any:
      "Face to face, email or phone counselling sessions. Contact us on 0800 317 810."
  },
  MentalHealthBusinessProtect: { 
    HOL:
      "Face to face, email or phone counselling sessions. Contact us on 0345 600 7696.",
    Any:
      "Face to face, email or phone counselling sessions. Contact us on 0345 600 7696."
  },
  ThumbnailListHeading: {
    Any: "Your plan also offers you",
    HOL: "Your policy also offers you"
  },
  ThumbnailListDescription:
    "The following additional services are also available as part of your membership.",
  ThumbNailListItems: {
    Format2: {
      Any: [
        Thumbnails.thumbNailHealthInformation,
        Thumbnails.thumbNailGPAppointments,
        Thumbnails.thumbNailFastTrackAppointments
      ],
      Lapsed: [
        Thumbnails.thumbNailHealthInformation,
        Thumbnails.thumbNailFastTrackAppointments
      ]
    },
    International: [
      Thumbnails.thumbNailHealthInformation,
      Thumbnails.thumbNailFindTreatment,
      Thumbnails.thumbNailFastTrackAppointments
    ],
    Saga: [Thumbnails.thumbNailHealthInformation],
    HOL: {
      HOL: [
        Thumbnails.thumbNailAccessHealthStoreHOL,
        Thumbnails.thumbNailAskAMedicalExpert],
      Lapsed: [
        Thumbnails.thumbNailAskAMedicalExpert
      ]
    },
    Any: {
      Any: [
        Thumbnails.thumbNailAccessHealthStore,
        Thumbnails.thumbNailAskAMedicalExpert,
        Thumbnails.thumbNailGPAppointments
      ],
      LargeCorporate: [
        Thumbnails.thumbNailAskAMedicalExpert,
        Thumbnails.thumbNailGPAppointments
      ],
      Lapsed: [
        Thumbnails.thumbNailAskAMedicalExpert
      ],
      FastTrack: {
        Any: [
          Thumbnails.thumbNailAccessHealthStore,
          Thumbnails.thumbNailAskAMedicalExpert,
          Thumbnails.thumbNailGPAppointments,
          Thumbnails.thumbNailFastTrackAppointments
        ],
        Lapsed: [
          Thumbnails.thumbNailAskAMedicalExpert,
          Thumbnails.thumbNailFastTrackAppointments
        ]
      }
    }
  },
  HelpSection: {
    HeadingLinkTo: Paths.Help,
    HeadingLinkText: "Help",
    MoreHelpLinkTo: Paths.Help,
    MoreHelpLinkText: "View more help",
    HelpSetForSMEModular: [HowToSeeWhoIsCovered, WhoAreCovered],
    HelpSetForHarpa: [
      HowToSeeWhoIsCovered,
      WhoAreCovered,
      HowDoIAddOtherOptions
    ],
    HelpSetForHOL: [
      HowToSeeWhoIsCovered,
      WhoAreCovered,
      HowDoIAddOtherOptions,
      HowDoIFindHospital
    ],
    HelpSetForHarpaIND: [
      HowToSeeWhoIsCovered,
      WhoAreCovered,
      HowDoIAddOtherOptionsInd
    ],
    HelpSetForHOLIND: [
      HowToSeeWhoIsCovered,
      WhoAreCovered,
      HowDoIAddOtherOptionsInd,
      HowDoIFindHospital
    ]
  }
};
